<template>
  <div>
    <div class="grid grid-cols-12 gap-6">
      <!-- Live Preview -->
      <div
        class="shadow rounded flex items-center relative justify-center col-span-12 lg:col-span-8 bg-white p-4"
        style="min-height: 300px"
      >
        <img
          v-if="floorplanType === '3D'"
          :src="`/static/grundrisse/assets/images/previews/3d/layout0${EinrichtungOption ? EinrichtungOption.id : 'layout01'}_${BodenbelagOption ? BodenbelagOption.id : '1'}.jpg`"
          alt=""
          class="w-full"
        />
        <img
          v-show="floorplanType === '2D' && Nordpfeil"
          src="/static/grundrisse/assets/images/previews/2d/nordpfeil.png"
          alt="Nordpfeil"
          class="absolute bottom-2 right-0"
        />
        <img
          v-show="floorplanType === '2D' && Massketten"
          src="/static/grundrisse/assets/images/previews/2d/massketten.png"
          alt="Maßketten"
          class="absolute top-0"
        />
        <img
          v-if="floorplanType === '2D'"
          :src="`/static/grundrisse/assets/images/previews/2d/preview${Farbe ? '-color' : ''}${
            Einrichtung ? '-furniture' : ''
          }.png`"
          alt=""
          class="w-full"
        />
      </div>

      <!-- Options -->
      <div class="flex flex-col col-span-12 lg:col-span-4">
        <!-- Einrichtung -->
        <div class="mb-4">
          <SwitchGroup>
            <div
              class="w-full flex flex-row justify-between items-center text-lg"
              v-show="floorplanType === '2D'"
            >
              <SwitchLabel>Einrichtung</SwitchLabel>
              <Switch
                v-model="Einrichtung"
                class="focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 flex items-center"
              >
                <span class="sr-only">Einrichtung</span>
                <span
                  v-if="floorplanType === '3D'"
                  class="text-sm mr-2"
                  :class="!Einrichtung ? 'text-secondary' : 'text-gray-400'"
                  >Ohne</span
                >
                <span
                  class="w-[56px] border-2 border-transparent rounded-full transition colors relative inline-flex flex-shrink-0 h-[24px] ease-in-out duration-200"
                  :class="Einrichtung ? 'bg-secondary' : 'bg-gray-400'"
                >
                  <span
                    aria-hidden="true"
                    :class="Einrichtung ? 'translate-x-8' : 'translate-x-0'"
                    class="pointer-events-none inline-block h-[20px] w-[20px] rounded-full bg-white shadow-lg transform ring-0 transition ease-in-out duration-200"
                  />
                </span>
                <span
                  v-if="floorplanType === '3D'"
                  class="text-sm ml-2"
                  :class="Einrichtung ? 'text-secondary' : 'text-gray-400'"
                  >Mit</span
                >
              </Switch>
            </div>
          </SwitchGroup>

          <span class="block text-lg mb-2">Einrichtungsstil</span>
          <RadioGroup
            v-model="EinrichtungOption"
            v-if="floorplanType === '3D'"
            :disabled="!Einrichtung"
          >
            <RadioGroupLabel class="sr-only">Wählen Sie Einrichtung</RadioGroupLabel>
            <div
              class="grid grid-cols-3 gap-6 mt-2"
              :class="Einrichtung ? '' : 'opacity-50 pointer-events-none'"
            >
              <RadioGroupOption
                as="template"
                v-for="fill in fills"
                :key="fill.name"
                :value="fill"
                v-slot="{ checked }"
              >
                <div class="flex flex-col cursor-pointer focus:outline-none">
                  <div
                    class="flex items-center justify-between w-full rounded-lg shadow-md h-[80px] overflow-hidden hover:scale-105 transition ease-in-out hover:shadow-lg"
                    :class="[checked ? 'scale-105 ring-2 ring-secondary' : '']"
                  >
                    <img
                      :src="'/static/grundrisse/assets/images/furniture/' + fill.id + '.png'"
                      :alt="fill.name"
                      class="h-full object-contain"
                    />
                  </div>
                  <span
                    class="text-center mt-1 text-gray-600"
                    :class="checked ? 'text-black' : 'text-gray-600'"
                  >
                    {{ fill.name }}
                  </span>
                </div>
              </RadioGroupOption>
            </div>
          </RadioGroup>
        </div>

        <!-- Bodenbelag -->
        <div v-if="floorplanType === '3D'" class="mt-6">
          <span class="block text-lg mb-2">Bodenbelag</span>

          <RadioGroup v-model="BodenbelagOption">
            <RadioGroupLabel class="sr-only">Wählen Sie Bodenbelag</RadioGroupLabel>
            <div class="grid grid-cols-3 gap-6">
              <RadioGroupOption
                as="template"
                v-for="floor in floors"
                :key="floor.name"
                :value="floor"
                v-slot="{ checked }"
              >
                <div class="flex flex-col cursor-pointer focus:outline-none">
                  <div
                    class="flex items-center justify-between w-full rounded-lg shadow-md h-[80px] overflow-hidden hover:scale-105 transition ease-in-out hover:shadow-lg"
                    :class="[
                      checked
                        ? 'scale-105 ring-2 ring-secondary'
                        : 'bg-white ',
                    ]"
                  >
                    <img
                      :src="'/static/grundrisse/assets/images/floorings/' + floor.id + '.png'"
                      :alt="floor.name"
                      class="object-cover w-full h-full"
                    />
                  </div>
                  <span
                    class="text-center mt-1 text-gray-600"
                    :class="checked ? 'text-black' : 'text-gray-600'"
                  >
                    {{ floor.name }}
                  </span>
                </div>
              </RadioGroupOption>
            </div>
          </RadioGroup>
        </div>

        <!-- Auflösung -->
        <div v-if="floorplanType === '3D'" class="mt-6">
          <SwitchGroup>
            <div
              class="w-full flex flex-row justify-between items-center text-lg mb-4"
            >
              <SwitchLabel>Auflösung</SwitchLabel>
              <Switch
                v-model="Auflosung"
                class="focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 flex items-center"
              >
                <span class="sr-only">Auflösung</span>
                <span
                  class="text-sm mr-2"
                  :class="!Auflosung ? 'text-secondary' : 'text-gray-400'"
                  >1080p</span
                >
                <span
                  class="w-[56px] border-2 border-transparent rounded-full transition colors relative inline-flex flex-shrink-0 h-[24px] ease-in-out duration-200"
                  :class="Auflosung ? 'bg-secondary' : 'bg-secondary'"
                >
                  <span
                    aria-hidden="true"
                    :class="Auflosung ? 'translate-x-8' : 'translate-x-0'"
                    class="pointer-events-none inline-block h-[20px] w-[20px] rounded-full bg-white shadow-lg transform ring-0 transition ease-in-out duration-200"
                  />
                </span>
                <span
                  class="text-sm ml-2"
                  :class="Auflosung ? 'text-secondary' : 'text-gray-400'"
                  >2160p</span
                >
              </Switch>
            </div>
          </SwitchGroup>
        </div>

        <!-- Nordpfeil -->
        <SwitchGroup v-if="floorplanType === '2D'">
          <div
            class="w-full flex flex-row justify-between items-center text-lg mb-4"
          >
            <SwitchLabel>Nordpfeil</SwitchLabel>
            <Switch
              v-model="Nordpfeil"
              :class="Nordpfeil ? 'bg-secondary' : 'bg-gray-400'"
              class="relative inline-flex flex-shrink-0 h-[24px] w-[56px] border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
            >
              <span class="sr-only">Nordpfeil</span>
              <span
                aria-hidden="true"
                :class="Nordpfeil ? 'translate-x-8' : 'translate-x-0'"
                class="pointer-events-none inline-block h-[20px] w-[20px] rounded-full bg-white shadow-lg transform ring-0 transition ease-in-out duration-200"
              />
            </Switch>
          </div>
        </SwitchGroup>

        <!-- Maßketten -->
        <SwitchGroup v-if="floorplanType === '2D'">
          <div
            class="w-full flex flex-row justify-between items-center text-lg mb-4"
          >
            <SwitchLabel>Maßketten</SwitchLabel>
            <Switch
              v-model="Massketten"
              :class="Massketten ? 'bg-secondary' : 'bg-gray-400'"
              class="relative inline-flex flex-shrink-0 h-[24px] w-[56px] border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
            >
              <span class="sr-only">Maßketten</span>
              <span
                aria-hidden="true"
                :class="Massketten ? 'translate-x-8' : 'translate-x-0'"
                class="pointer-events-none inline-block h-[20px] w-[20px] rounded-full bg-white shadow-lg transform ring-0 transition ease-in-out duration-200"
              />
            </Switch>
          </div>
        </SwitchGroup>

        <!-- Farbe -->
        <SwitchGroup v-if="floorplanType === '2D'">
          <div
            class="w-full flex flex-row justify-between items-center text-lg mb-4"
          >
            <SwitchLabel>Farbe</SwitchLabel>
            <Switch
              v-model="Farbe"
              :class="Farbe ? 'bg-secondary' : 'bg-gray-400'"
              class="relative inline-flex flex-shrink-0 h-[24px] w-[56px] border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
            >
              <span class="sr-only">Farbe</span>
              <span
                aria-hidden="true"
                :class="Farbe ? 'translate-x-8' : 'translate-x-0'"
                class="pointer-events-none inline-block h-[20px] w-[20px] rounded-full bg-white shadow-lg transform ring-0 transition ease-in-out duration-200"
              />
            </Switch>
          </div>
        </SwitchGroup>
        <div class="w-full text-right mt-auto text-gray-600">
          Preis pro Grundriss
          <span class="font-bold text-lg text-black">{{
            formatter.format(unitPrice)
          }}</span>
        </div>
      </div>
    </div>

    <div class="mt-12 flex items-center mb-4">
      <h2 class="text-xl font-medium">Laden Sie Ihre Grundrisse hoch</h2>
      <span class="text-sm text-gray-400 ml-2">(PNG, PDF, JPEG)</span>
    </div>

    <!-- FILE UPLOAD -->
    <div
      @drop="handleFileDrop"
      @dragover="handleDragOver"
      @dragenter="handleDragEnter"
      @dragleave="handleDragEnd"
      class="relative"
    >
      <div
        v-if="dragOverlayShown"
        class="absolute top-0 flex flex-col text-center items-center py-16 w-full h-full bg-secondary-60 bg-opacity-90 rounded"
      >
        <UploadIcon class="w-[40px] text-white mb-4 animate-bounce" />
        <span class="text-lg font-medium text-white">Dateien per Drag & Drop hochladen</span>
      </div>
      <div
        class="py-8 border-dashed border-2 border-gray-300 rounded mb-4 text-center flex flex-col items-center"
        :class="errors.uploads && 'border-red-500'"
      >
        <PhotographIcon class="w-[40px] text-gray-400 mb-4" />
        <div>
          <div class="text-lg font-medium mb-6 text-gray-600">
						Legen Sie Ihre Dateien hier ab oder klicken Sie auf "Grundriss hinzufügen"
          </div>
          <button
            @click="handleClickUpload"
            class="bg-accent px-6 py-2 rounded shadow text-white"
          >
						Grundriss hinzufügen
          </button>
          <input
            type="file"
            @change="onFileChange"
            multiple
            accept="image/png, image/jpeg, application/pdf"
            class="hidden"
            ref="fileInput"
          />
        </div>
      </div>
      <canvas ref="renderCanvas" class="hidden" />
      <div
        v-for="(upload, uploadId) in uploads"
        :key="uploadId"
        class="px-2 py-2 shadow rounded items-center mb-2"
      >
        <div class="flex items-center">
          <img
            class="h-[128px] w-[128px] mr-4 object-cover rounded"
            :src="upload.image"
            :alt="upload.title + ' preview'"
          />
          <span class="text-ellipsis overflow-hidden">{{ upload.title }}</span>
          <div class="ml-auto text-sm flex flex-row">
            <a
              @click="addDescription(uploadId)"
              v-if="upload.description === null"
              class="cursor-pointer mr-4 hover:underline flex items-center text-secondary"
              ><AnnotationIcon class="w-[18px] mr-1" />Änderungswünsche hinzufügen (zzgl. 8€)</a
            >
            <a
              @click="removeDescription(uploadId)"
              v-if="upload.description !== null"
              class="cursor-pointer mr-4 hover:underline flex items-center text-secondary"
              ><AnnotationIcon class="w-[18px] mr-1" />Änderungswünsche entfernen</a
            >
            <a
              @click="removeFloorplan(uploadId)"
              class="cursor-pointer flex items-center text-gray-600 hover:text-red-700 hover:underline"
              ><XIcon class="w-[18px] mr-1" />Datei entfernen</a
            >
          </div>
        </div>
        <div
          v-if="upload.description !== null"
          class="mt-4 flex flex-col text-center"
        >
          <textarea
            v-model="upload.description"
            class="rounded border border-gray-400"
            placeholder='Beschreiben Sie hier (kostenpflichtige) Änderungswünsche, bspw. ̇"Wohnzimmertür um 1m nach links verschieben", "Küchentür öffnet anders herum". Alternativ können Sie Änderungen auch (kostenfrei) in den bestehenden Grundriss einzeichnen und diesen erneut hochladen.'
          />
        </div>
      </div>

      <!-- Uploading file skeleton -->
      <div
        v-for="(item, itemId) in filesUploading"
        :key="itemId"
        class="px-6 py-6 shadow rounded items-center mb-2 flex"
      >
        <svg
          class="animate-spin -ml-1 mr-3 h-5 w-5 text-brand"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        Uploading {{ item }}
      </div>

      <div v-show="errors.uploads" class="text-center text-red-500">
        {{ errors.uploads }}
      </div>
    </div>

    <div
      class="flex flex-col pt-5 border-t mt-5 border-top-gray-600 text-right"
    >
      <div class="text-right ml-auto flex">
        <label for="floorplan-count" class="mr-3">
          <span class="font-semibold" :class="{ 'text-red-500': errors.units }"
            >Anzahl Grundrisse</span
          >
          <p class="text-sm">
            1 Grundriss entspricht einer Etage, einer Wohneinheit und einer
            Fläche von max. 200m²
          </p>
        </label>
        <div class="relative">
          <input
            type="number"
            class="w-[80px] bg-yellow-150 mt-1 block border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-1 focus:outline-none focus:ring-indigo-500 focus:border-secondary sm:text-sm"
            min="0"
            v-model="units"
            name="floorplan-count"
            :class="{ 'border-red-400 pr-8': errors.units }"
          />
          <div
            v-if="errors.units"
            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
          >
            <ExclamationCircleIcon
              class="h-5 w-5 text-red-400"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
      <span v-show="errors.units" class="text-red-500 mt-1">{{
        errors.units
      }}</span>
      <span class="text-2xl font-semibold mt-3">{{
        formatter.format(overallPrice)
      }}</span>
    </div>
  </div>
</template>

<script>
import {
  useForm,
  useField,
  useIsFormValid,
  useIsFormDirty,
  useFormValues,
  useValidateForm,
} from "vee-validate";
import * as yup from "yup";

import * as PDFJS from "pdfjs-dist";
PDFJS.GlobalWorkerOptions.workerSrc = "/static/grundrisse/pdf.worker.js";

import {
  Switch,
  SwitchGroup,
  SwitchLabel,
  RadioGroup,
  RadioGroupDescription,
  RadioGroupLabel,
  RadioGroupOption,
} from "@headlessui/vue";
import {
  XIcon,
  PhotographIcon,
  AnnotationIcon,
  CheckCircleIcon,
  UploadIcon,
  ExclamationCircleIcon,
} from "@heroicons/vue/outline";

import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import axios from "axios";

const API_URL_UPLOAD = import.meta.env.VITE_API_URL_UPLOAD;

const fills = [
  {
    name: "1",
    id: "1",
  },
  {
    name: "2",
    id: "2",
  },
  {
    name: "3",
    id: "3",
  },
  {
    name: "4",
    id: "4",
  },
  {
    name: "5",
    id: "5",
  },
];

const floors = [
  {
    name: "Parkett Hell",
    id: "1",
  },
  {
    name: "Parkett Dunkel",
    id: "2",
  },
  {
    name: "Parkett Eiche",
    id: "5",
  },
  {
    name: "Fliese Weiß",
    id: "3",
  },
  {
    name: "Fliese Grau",
    id: "4",
  },
];

var formatter = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
});

const hasFiles = ({ dataTransfer: { types = [] } }) =>
  types.indexOf("Files") > -1;

// This is used to help with dragover overlay display and hide events
let counter = 0;

export default {
  components: {
    Switch,
    SwitchGroup,
    SwitchLabel,
    RadioGroup,
    RadioGroupDescription,
    RadioGroupLabel,
    RadioGroupOption,
    XIcon,
    PhotographIcon,
    AnnotationIcon,
    CheckCircleIcon,
    UploadIcon,
    ExclamationCircleIcon,
  },
  emits: ["changed"],
  methods: {
    onFileChange(event) {
      var files = event.target.files || event.dataTransfer.files;
      if (!files.length) return;

      for (const file of files) {
        this.addFile(file);
      }
    },
    async addFile(file) {
      const tempFormData = new FormData();
      tempFormData.append("file", file);

      this.filesUploading.push(file.name);

      try {
        const response = await axios.post(`${API_URL_UPLOAD}`, tempFormData);

        if (response.status === 200) {
          if (file.type === "application/pdf") {
            const loadingTask = PDFJS.getDocument(URL.createObjectURL(file));

            const pdfFile = await loadingTask.promise;

            const context = this.$refs.renderCanvas.getContext("2d");
            const totalPages = pdfFile.numPages;

            for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
              try {
                const page = await pdfFile.getPage(pageNumber);

                const viewport = page.getViewport({ scale: 1 });
                this.$refs.renderCanvas.height = viewport.height;
                this.$refs.renderCanvas.width = viewport.width;

                const renderContext = {
                  canvasContext: context,
                  viewport: viewport,
                };

                const renderTask = page.render(renderContext);
                await renderTask.promise;

                this.uploads.push({
                  filename: response.data.filename,
                  title: `${file.name} (Seite ${pageNumber})`,
                  localFilename: file.name,
                  pageNumber: pageNumber,
                  image: this.$refs.renderCanvas.toDataURL("image/png"),
                  description: null,
                });
              } catch (pdfErr) {
                console.error("Error getting PDF page:", pdfErr);
              }
            }
          } else {
            this.uploads.push({
              filename: response.data.filename,
              title: file.name,
              localFilename: file.name,
              image: URL.createObjectURL(file),
              description: null,
            });
          }
        }
      } catch (err) {
        console.error("Error uploading file", err);
      } finally {
        const itemIndex = this.filesUploading.indexOf(file.name);
        if (itemIndex > -1) this.filesUploading.splice(itemIndex, 1);
      }
    },
    removeFloorplan(index) {
      this.uploads.splice(index, 1);

      if (this.uploads.length === 0) {
        this.$refs.fileInput.value = null;
      }
    },
    addDescription(index) {
      this.uploads[index].description = "";
    },
    removeDescription(index) {
      this.uploads[index].description = null;
			this.descriptions--
    },
    handleFileDrop(event) {
      event.preventDefault();
      for (const file of event.dataTransfer.files) {
        this.addFile(file);
      }
      this.dragOverlayShown = false;
      counter = 0;
    },
    handleDragOver(event) {
      if (hasFiles(event)) {
        event.preventDefault();
      }
    },
    handleDragEnter(event) {
      event.preventDefault();
      if (!hasFiles(event)) {
        return;
      }

      ++counter;
      this.dragOverlayShown = true;
    },
    handleDragEnd() {
      --counter;
      if (counter === 0) this.dragOverlayShown = false;
    },
    handleClickUpload() {
      this.$refs.fileInput.click();
    },
    enableFurniture() {
      this.Einrichtung.value = ref(true);
    },
  },
  setup(_, { emit }) {
    const dragOverlayShown = ref(false);

    const store = useStore();

    const schema = computed(() => {
      return yup.object({
        Einrichtung: yup.boolean(),
        Nordpfeil: yup.string(),
        Massketten: yup.string(),
        Farbe: yup.string(),
        uploads: yup.array().min(1, "Bitte laden Sie einen Grundriss hoch"),
        units: yup
          .number()
          .min(1, "Bitte geben Sie die Anzahl der zu zeichnenden Grundrisse an."),
      });
    });

    const { errors, setValues } = useForm({
      validationSchema: schema,
      initialValues: {
        units: 0,
				descriptions: 0,
        uploads: [],
				Einrichtung: true,
				Bodenbelag: false,
				Auflosung: false,
				Massketten: false,
				Nordpfeil: false,
				Farbe: false,
        unitPrice: 0,
        overallPrice: 0,
      },
    });

    const isValid = useIsFormValid();
    const isDirty = useIsFormDirty();
    const formValues = useFormValues();
    const validate = useValidateForm();

    const { value: EinrichtungOption } = useField("EinrichtungOption");
    const { value: BodenbelagOption } = useField("BodenbelagOption");
    const { value: Einrichtung } = useField("Einrichtung");
    const { value: Nordpfeil } = useField("Nordpfeil");
    const { value: Massketten } = useField("Massketten");
    const { value: Farbe } = useField("Farbe");
    const { value: Bodenbelag } = useField("Bodenbelag");
    const { value: Auflosung } = useField("Auflosung");
    const { value: uploads } = useField("uploads");
    const { value: units } = useField("units");
    const { value: descriptions } = useField("descriptions");
    const { value: unitPrice } = useField("unitPrice");
    const { value: overallPrice } = useField("overallPrice");

    const formDataValues = computed(() => {
      return { ...store.state.steps.formData };
    });

    const formData = computed(() => {
      return {
        formValues: { ...formValues.value },
        isValid: isValid.value,
        isDirty: isDirty.value,
        validate: validate,
      };
    });

    /* Selected = 2D or 3D, based on Step One selection */
    const floorplanType = formDataValues.value.floorplanType;

    /* Initialize array for UI when uploading files */
    const filesUploading = ref([]);

    /* On mount, set initial values */
    onMounted(() => {
      setValues(formDataValues.value);

      if (!EinrichtungOption.value) {
        EinrichtungOption.value = ref(fills[0]);
      }

      if (!BodenbelagOption.value) {
        BodenbelagOption.value = ref(floors[0]);
      }

      if (unitPrice.value === 0) {
        unitPrice.value = floorplanType === "3D" ? 37 : 24;
      }
    });

    /* Form Changes */
    watch(
      () => formData,
      (data) => {
        emit("changed", data);
      },
      { deep: true }
    );

    watch(
      () => Auflosung.value,
      (newValue) => {
        /* If Auflosung is changed to 2160p, add additional price */
        if (newValue === true) {
          unitPrice.value = unitPrice.value + 8;
        }

        /* If Auflosung is changed to 1080p, remove additional price */
        if (newValue === false) {
          unitPrice.value = unitPrice.value - 8;
        }
      },
      { deep: true }
    );

    const calculateNewPrice = () => {
      overallPrice.value = units.value > 0 ? unitPrice.value * units.value + (descriptions.value * 8) : 0
    };

    watch(
      () => Farbe.value,
      (newValue) => {
        /* If Farbe is changed to true, add additional price */
        if (newValue === true) {
          unitPrice.value = unitPrice.value + 3;
        }

        /* If Farbe is changed to false, remove additional price */
        if (newValue === false) {
          unitPrice.value = unitPrice.value - 3;
        }
      },
      { deep: true }
    );
    
    watch(
      () => uploads.value,
      (newValue, oldValue) => {
        /* If an upload object description property is not empty, increase the number of descriptions */
        let descriptionCount = 0;
        newValue.map((value, i) => {
          if (value.description && value.description !== null && value.description !== "") {
            descriptionCount++
          }
        })

        descriptions.value = descriptionCount;
      },
      { deep: true }
    )

    watch(
      () => [descriptions.value, units.value, unitPrice.value],
      () => {
        calculateNewPrice()
      },
      { deep: true }
    );

    return {
      formDataValues,
      errors,
      formData,
      Einrichtung,
      Nordpfeil,
      Massketten,
      Farbe,
      floorplanType,
      EinrichtungOption,
      fills,
      floors,
      Bodenbelag,
      BodenbelagOption,
      Auflosung,
      uploads,
      filesUploading,
      unitPrice,
      overallPrice,
      units,
      descriptions,
      formatter,
      dragOverlayShown,
    };
  },
};
</script>
